import { useState, useEffect } from "react";
import { Link } from "react-router";
import { Instagram } from "lucide-react";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { useTheme } from "./ThemeInjector";
import defaultLogoSvg from "../../imports/MoechaLogo.svg";

// Inline SVG icons for social platforms not in lucide-react
function TikTokIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}
function XTwitterIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4l11.733 16h4.267l-11.733 -16z" />
      <path d="M4 20l6.768 -6.768m2.46 -2.46L20 4" />
    </svg>
  );
}
function FacebookIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}
function YoutubeIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </svg>
  );
}
function PinterestIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="11" x2="12" y2="21" />
      <path d="M9.51 16.2C5.45 15.17 3 12.14 3 8.5 3 4.36 6.36 1 10.5 1c4.14 0 7.5 3.36 7.5 7.5 0 3.64-2.45 6.67-6.49 7.7" />
      <path d="M8 21l1.5-6" />
    </svg>
  );
}
function LinkedinIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

const SOCIAL_ICON_MAP: Record<string, React.FC<{ size?: number }>> = {
  instagram: Instagram,
  tiktok: TikTokIcon,
  twitter: XTwitterIcon,
  facebook: FacebookIcon,
  youtube: YoutubeIcon,
  pinterest: PinterestIcon,
  linkedin: LinkedinIcon,
};

const SOCIAL_LABELS: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  twitter: "X (Twitter)",
  facebook: "Facebook",
  youtube: "YouTube",
  pinterest: "Pinterest",
  linkedin: "LinkedIn",
};

export function Footer() {
  const { siteConfig } = useAdminData();
  const { t, lang } = useLanguage();
  const theme = useTheme();
  const logo = siteConfig.logo || defaultLogoSvg;
  const socialLinks = siteConfig.socialLinks || {};
  const sloganEN = siteConfig.slogan || "Crafted with Intention. Shared with Passion.";
  const slogan = lang === "en"
    ? sloganEN
    : (siteConfig.sloganI18n?.[lang as "ja" | "nl" | "fr"]?.slogan || sloganEN);

  const activeSocials = Object.entries(socialLinks).filter(
    ([, url]) => url && url.trim().length > 0
  );

  const navLinks = [
    { label: t("nav.shop"), to: "/shop" },
    { label: t("nav.workshops"), to: "/workshops" },
    { label: t("nav.stories"), to: "/journal" },
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.wholesale"), to: "/wholesale" },
    { label: t("nav.press"), to: "/press" },
    { label: t("nav.policies"), to: "/policies" },
  ];
  const column1Links = navLinks.slice(0, 4); // Shop, Workshops, Stories, About
  const column2Links = navLinks.slice(4, 7);   // Wholesale, Press, Policies

  const linkStyle = {
    fontFamily: theme.fontBody,
    fontSize: "12px",
    fontWeight: 300,
    color: `${theme.bg}99`,
  };

  return (
    <footer className="py-20 px-6" style={{ backgroundColor: theme.fg, color: `${theme.bg}cc` }}>
      <div className="max-w-7xl mx-auto">
        {/* 1 col (narrow) → 3 col (md) → 4 col (lg). Narrow: one link list, no break. */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8 md:items-center lg:grid-cols-4 lg:gap-12 lg:items-start">
          {/* Column 1: Logo + contact (when set) */}
          <div className="flex flex-col justify-center md:justify-start gap-4">
            <img
              src={logo}
              alt="MOECHA"
              className="h-10 object-contain brightness-0 invert"
            />
            {(siteConfig.contact_email || siteConfig.contact_phone || siteConfig.contact_address) && (
              <div className="flex flex-col gap-1.5" style={{ ...linkStyle, fontSize: "11px" }}>
                {siteConfig.contact_email && (
                  <a href={`mailto:${siteConfig.contact_email.trim()}`} className="hover:underline" style={{ color: "inherit" }}>
                    {siteConfig.contact_email.trim()}
                  </a>
                )}
                {siteConfig.contact_phone && (
                  <a href={`tel:${siteConfig.contact_phone.replace(/\s/g, "")}`} className="hover:underline" style={{ color: "inherit" }}>
                    {siteConfig.contact_phone.trim()}
                  </a>
                )}
                {siteConfig.contact_address && (
                  <span className="whitespace-pre-line">{siteConfig.contact_address.trim()}</span>
                )}
              </div>
            )}
          </div>

          {/* Column 2 (md = center links; lg = first link column) */}
          <div className="flex flex-col items-center gap-4 md:col-span-1 lg:items-start">
            {/* Single block of all links — visible on narrow and md, hidden on lg */}
            <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4 md:justify-center lg:hidden">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="tracking-[0.2em] uppercase transition-colors duration-200 block"
                  style={linkStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = `${theme.bg}99`)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            {/* First link column — visible only on lg (4-col) */}
            <div className="hidden lg:flex flex-col gap-4">
              {column1Links.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="tracking-[0.2em] uppercase transition-colors duration-200"
                  style={linkStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = `${theme.bg}99`)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Column 3: Second link column — only on lg (4-col) */}
          <div className="hidden lg:flex flex-col gap-4">
            {column2Links.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="tracking-[0.2em] uppercase transition-colors duration-200"
                style={linkStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
                onMouseLeave={(e) => (e.currentTarget.style.color = `${theme.bg}99`)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Column 4 (md) / Column 4 (lg): Social & slogan */}
          <div className="flex flex-col items-center gap-6 md:items-end">
            {activeSocials.length > 0 && (
              <div className="flex items-center gap-3">
                {activeSocials.map(([key, url]) => {
                  const Icon = SOCIAL_ICON_MAP[key];
                  if (!Icon) return null;
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-colors duration-200"
                      style={{ color: `${theme.bg}99` }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = `${theme.bg}99`)}
                      aria-label={SOCIAL_LABELS[key] || key}
                    >
                      <Icon size={20} />
                    </a>
                  );
                })}
              </div>
            )}
            <p
              className="text-center md:text-right"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", fontWeight: 300, color: `${theme.bg}66` }}
            >
              {slogan.includes(". ") ? (
                <>
                  {slogan.split(". ")[0]}.
                  <br />
                  {slogan.split(". ").slice(1).join(". ")}
                </>
              ) : (
                slogan
              )}
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 text-center" style={{ borderTop: `1px solid ${theme.bg}1a` }}>
          <p
            className="tracking-[0.1em]"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", fontWeight: 300, color: `${theme.bg}4d` }}
          >
            &copy; {new Date().getFullYear()} MOECHA. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}